import type { Course } from '../services/courseService';
import { CourseType } from '../services/courseService';

// Canonical delivery-method tokens for credit limits (caps). Mirrors the
// backend's DeliveryMethodTokens + CreditDeliveryMethodClassifier — keep in sync.

export const DELIVERY_METHOD_TOKENS = [
  'group_live',
  'group_internet_based',
  'self_study',
  'nano_learning',
  'blended_learning',
  'instructed',
  'professional_journals',
  'published_materials',
  'reviewer',
] as const;

export type DeliveryMethodToken = (typeof DELIVERY_METHOD_TOKENS)[number];

/** Token → display label. Labels match the third-party credit dialog dropdown. */
const TOKEN_LABELS: Record<DeliveryMethodToken, string> = {
  group_live: 'Group Live',
  group_internet_based: 'Group Internet-Based',
  self_study: 'Self-Study',
  nano_learning: 'Nano Learning',
  blended_learning: 'Blended Learning',
  instructed: 'Instructed',
  professional_journals: 'Professional Journals',
  published_materials: 'Published Materials',
  reviewer: 'Reviewer',
};

export function getDeliveryMethodLabel(token: string): string {
  return TOKEN_LABELS[token as DeliveryMethodToken] ?? token;
}

/** Joined display labels for a limit's method set, e.g. "Self-Study, Nano Learning". */
export function formatDeliveryMethods(tokens: string[]): string {
  return tokens.map(getDeliveryMethodLabel).join(', ');
}

// Course.meetingFormat / meetingPresentationFormat numeric values (backend enums).
const MEETING_FORMAT_ONLINE = 1;
const MEETING_FORMAT_IN_PERSON = 2;
const PRESENTATION_FORMAT_LIVE = 1;
const PRESENTATION_FORMAT_PRERECORDED = 2;
const PRESENTATION_FORMAT_SELF_PACED = 3;

/**
 * Classifies a platform course's delivery method for cap purposes. Mirrors the
 * backend CreditDeliveryMethodClassifier.ClassifyPlatform: SelfPaced/Prerecorded
 * ⇒ self-study; Live ⇒ group study split by meeting format; unset formats fall
 * back to OnDemand ⇒ self-study. Returns null = unclassified (exempt from caps).
 */
export function classifyCourseDeliveryMethod(
  course: Pick<Course, 'courseType' | 'meetingFormat' | 'meetingPresentationFormat'>
): DeliveryMethodToken | null {
  switch (course.meetingPresentationFormat) {
    case PRESENTATION_FORMAT_SELF_PACED:
    case PRESENTATION_FORMAT_PRERECORDED:
      return 'self_study';
    case PRESENTATION_FORMAT_LIVE:
      switch (course.meetingFormat) {
        case MEETING_FORMAT_IN_PERSON:
          return 'group_live';
        case MEETING_FORMAT_ONLINE:
          return 'group_internet_based';
        default:
          return null;
      }
    default:
      return course.courseType === CourseType.OnDemand ? 'self_study' : null;
  }
}
